import { useState } from "react";
import { Link } from "@inertiajs/react";
import { SearchBar } from "@components/job_search/search_bar.tsx";
import { type Location } from "@components/ui/location_input.tsx";
import { type RecentSearch } from "@/types/job_search/recent_searches";
import { useJobSearch, buildJobsParams, recentSearchToLocation } from "@hooks/use_job_search";
import { JobSearchFilters } from "@components/job_search/job_search_filters.tsx";
import { SectionTitle } from "@/sprout/typography";
import ArrowRightIcon from "@/sprout/icons/arrow_right";
import useTranslation from "@hooks/use_translation";
import "./job_search_widget.scss";

type Props = {
  recentSearches: RecentSearch[];
};

const JobSearchWidget = ({ recentSearches = [] }: Props) => {
  const { t } = useTranslation("jobSearch");
  const mostRecentSearch = recentSearches[0];

  const [location, setLocation] = useState<Location | null>(
    mostRecentSearch ? recentSearchToLocation(mostRecentSearch) : null,
  );
  const [query, setQuery] = useState(mostRecentSearch?.query || "");
  const [loading, setLoading] = useState(false);
  const [datePosted, setDatePosted] = useState(mostRecentSearch?.date_posted || "");
  const [salary, setSalary] = useState(mostRecentSearch?.salary || "");
  const [workType, setWorkType] = useState<string[]>(mostRecentSearch?.work_type || []);
  const [employmentType, setEmploymentType] = useState<string[]>(
    mostRecentSearch?.employment_type || [],
  );
  const submitJobSearch = useJobSearch();

  const handleSearch = () => {
    setLoading(true);

    submitJobSearch(
      buildJobsParams(query, location, {
        date_posted: datePosted,
        salary,
        work_type: workType,
        employment_type: employmentType,
      }),
      { onFinish: () => setLoading(false) },
    );
  };

  const handleRecentSearchSelect = (search: RecentSearch) => {
    setLoading(true);

    submitJobSearch(
      buildJobsParams(search.query || undefined, recentSearchToLocation(search), search),
      { onFinish: () => setLoading(false) },
    );
  };

  return (
    <div className="job-search-card">
      <Link href="/jobs" className="job-search-card__header">
        <SectionTitle>{t("header")}</SectionTitle>
        <div className="job-search-card__header__icon">
          <ArrowRightIcon />
        </div>
      </Link>
      <div className="job-search-card__search">
        <SearchBar
          onSearch={handleSearch}
          query={query}
          onQueryChange={setQuery}
          location={location}
          onLocationChange={setLocation}
          disabled={loading}
          recentSearches={recentSearches}
          onRecentSearchSelect={handleRecentSearchSelect}
        />
      </div>
      <div className="job-search-card__filters">
        <JobSearchFilters
          datePosted={datePosted}
          onDatePostedChange={setDatePosted}
          salary={salary}
          onSalaryChange={setSalary}
          workType={workType}
          onWorkTypeChange={setWorkType}
          employmentType={employmentType}
          onEmploymentTypeChange={setEmploymentType}
        />
      </div>
    </div>
  );
};

export default JobSearchWidget;
